// TODO: Figure out why RTK-Query never leaves the pending state, and never persists the data to the store
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios, { AxiosError } from "axios";
import type { RootState } from "src/redux";
import { buildStaffResourcePathForCurrentUser } from "src/redux/ResourceBuilder";

export type LabcorpBodySitePair = {
    key: string;
    description: string;
};

export type LabcorpBodySitesSliceState = {
    bodySites: LabcorpBodySitePair[];
    bodySiteDescriptors: LabcorpBodySitePair[];
    isLoadingBodySites: boolean;
    isLoadingBodySiteDescriptors: boolean;
    bodySitesError?: AxiosError;
    bodySiteDescriptorsError?: AxiosError;
};

export const getLabcorpBodySites = createAsyncThunk<
    LabcorpBodySitePair[],
    void,
    { rejectValue: AxiosError }
>("labcorp/getBodySites", async (_, thunkAPI) => {
    const url = buildStaffResourcePathForCurrentUser("labs/orders/labcorp/body-sites");
    try {
        const response = await axios.get<LabcorpBodySitePair[]>(url, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        const axiosError = error as AxiosError;
        return thunkAPI.rejectWithValue(axiosError);
    }
});

export const getLabcorpBodySiteDescriptors = createAsyncThunk<
    LabcorpBodySitePair[],
    void,
    { rejectValue: AxiosError }
>("labcorp/getBodySiteDescriptors", async (_, thunkAPI) => {
    const url = buildStaffResourcePathForCurrentUser("labs/orders/labcorp/body-sites/descriptors");
    try {
        const response = await axios.get<LabcorpBodySitePair[]>(url, {
            withCredentials: true,
        });
        return response.data;
    } catch (error) {
        const axiosError = error as AxiosError;
        return thunkAPI.rejectWithValue(axiosError);
    }
});

const initialState: LabcorpBodySitesSliceState = {
    bodySites: [],
    bodySiteDescriptors: [],
    isLoadingBodySites: false,
    isLoadingBodySiteDescriptors: false,
};
const labcorpBodySitesSlice = createSlice({
    name: "labcorp/body-sites",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(getLabcorpBodySites.pending, (state) => {
            state.isLoadingBodySites = true;
            state.bodySitesError = undefined;
        });
        builder.addCase(getLabcorpBodySites.fulfilled, (state, action) => {
            state.isLoadingBodySites = false;
            state.bodySites = action.payload;
        });
        builder.addCase(getLabcorpBodySites.rejected, (state, action) => {
            state.isLoadingBodySites = false;
            state.bodySitesError = action.error as AxiosError;
        });
        builder.addCase(getLabcorpBodySiteDescriptors.pending, (state) => {
            state.isLoadingBodySiteDescriptors = true;
            state.bodySiteDescriptorsError = undefined;
        });
        builder.addCase(getLabcorpBodySiteDescriptors.fulfilled, (state, action) => {
            state.isLoadingBodySiteDescriptors = false;
            state.bodySiteDescriptors = action.payload;
        });
        builder.addCase(getLabcorpBodySiteDescriptors.rejected, (state, action) => {
            state.isLoadingBodySiteDescriptors = false;
            state.bodySiteDescriptorsError = action.error as AxiosError;
        });
    },
});

export const selectBodySites = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].bodySites;
export const selectBodySiteDescriptors = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].bodySiteDescriptors;
export const selectIsLoadingBodySites = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].isLoadingBodySites;
export const selectIsLoadingBodySiteDescriptors = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].isLoadingBodySiteDescriptors;
export const selectBodySitesError = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].bodySitesError;
export const selectBodySiteDescriptorsError = (state: RootState) =>
    state[labcorpBodySitesSlice.reducerPath].bodySiteDescriptorsError;

export default labcorpBodySitesSlice;
